import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Layers, FlipHorizontal2, Shuffle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { generateVariations } from "../../lib/variations";
import { TrackBadge } from "../TrackBadge";

const poolLabel = (pool) => (pool === "challenge" ? "Bonus challenge pool" : "Warm-up pool");

const JUNIOR_STRATEGIES = [
  {
    id: "symmetry", icon: FlipHorizontal2, title: "Lite: reflect & rotate",
    blurb: "Mirrors and rotates the original grid. Same obstacles, same route length and turns - only the orientation changes. Instant.",
  },
  {
    id: "random", icon: Shuffle, title: "Auto-solver re-lay (current approach)",
    blurb: "Places the start, treasure and obstacles afresh, auto-solves every layout and keeps only those that play like the original. Up to ~40 seconds.",
  },
];

const PROGRESS_HINT = {
  junior: { symmetry: "Each mirrored / rotated layout is auto-solved before it is saved.", random: "Each layout is auto-solved and kept only if it plays like the original." },
};

// Junior only: pick how the 5 grids are made before anything runs.
function StrategyPicker({ onPick }) {
  return (
    <div className="space-y-2" data-testid="variations-strategy-picker">
      <p className="text-sm font-medium">How should the new grids be made?</p>
      {JUNIOR_STRATEGIES.map(({ id, icon: Icon, title, blurb }) => (
        <button
          key={id} type="button" onClick={() => onPick(id)}
          className="w-full flex items-start gap-3 rounded-xl border border-border bg-card hover:border-primary/60 hover:bg-primary/5 p-4 text-left transition-colors"
          data-testid={`variations-strategy-${id}`}
        >
          <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{blurb}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

const createdSubtitle = (v) => {
  if (v.mode !== "junior" || !v._stats) return (v.description || "").slice(0, 110);
  const stats = `${v._stats.moves} moves · ${v._stats.turns} turns · ${v._stats.blocks_used} blocks`;
  return v._stats.transform ? `${v._stats.transform} · ${stats}` : stats;
};

// Runs "Generate 5 similar problems" for one question and shows what was created / skipped.
export const VariationsDialog = ({ question, open, onClose, onCreated }) => {
  const [stage, setStage] = useState("");
  const [choice, setChoice] = useState(null); // { qid, strategy } - tied to the question so a previous pick can't leak into the next one
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const runningFor = useRef(null);
  const qid = question?.question_id;
  const isJunior = question?.mode === "junior";
  const strategy = choice && choice.qid === qid ? choice.strategy : null;

  useEffect(() => {
    if (!open) {
      setChoice(null);
      runningFor.current = null;
      return;
    }
    if (!qid) return;
    setResult(null);
    setError(null);
    setChoice(isJunior ? null : { qid, strategy: "ai" });
  }, [open, qid, isJunior]);

  useEffect(() => {
    if (!open || !qid || !strategy || runningFor.current === qid) return;
    runningFor.current = qid;
    generateVariations(question, { strategy, onStage: setStage })
      .then((res) => {
        setResult(res);
        if (res.created.length) onCreated?.(res.created);
      })
      .catch((e) => setError(e.message))
      .finally(() => setStage(""));
  }, [open, qid, strategy, question, onCreated]);

  const close = () => {
    if (stage) return; // don't close mid-run
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg" data-testid="variations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-primary" /> Generate 5 similar problems</DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground">{question?.title}</span>
            {question && <TrackBadge mode={question.mode} showPython />}
            <span>→ {poolLabel(question?.pool)}, titled "{question?.title}_a" … "_e", placed right after it in the question order</span>
          </DialogDescription>
        </DialogHeader>

        {isJunior && !strategy && <StrategyPicker onPick={(id) => setChoice({ qid, strategy: id })} />}

        {stage && (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-4" data-testid="variations-progress">
            <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
            <div>
              <p className="text-sm font-medium">{stage}</p>
              <p className="text-xs text-muted-foreground">{PROGRESS_HINT[question?.mode]?.[strategy] || "This can take up to a minute. Every solution is run against its own test cases before it is saved."}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm" data-testid="variations-error">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="variations-result">
            <p className="text-sm font-medium">{result.message}</p>
            {result.created.length > 0 && (
              <ul className="space-y-1.5" data-testid="variations-created">
                {result.created.map((v) => (
                  <li key={v.question_id} className="flex items-start gap-2 text-sm rounded-lg border border-emerald-400/40 bg-emerald-500/5 px-3 py-2" data-testid={`variation-created-${v.question_id}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium">{v.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{createdSubtitle(v)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {result.skipped.length > 0 && (
              <details className="text-xs text-muted-foreground" data-testid="variations-skipped">
                <summary className="cursor-pointer">{result.skipped.length} attempt{result.skipped.length !== 1 ? "s" : ""} skipped</summary>
                <ul className="mt-1 space-y-1 pl-4 list-disc">
                  {result.skipped.map((s, i) => <li key={i}>{s.title ? `${s.title}: ` : ""}{s.reason}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={close} disabled={!!stage} data-testid="variations-close-btn">{stage ? "Working…" : (isJunior && !strategy ? "Cancel" : "Done")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
